import * as THREE from "three/webgpu";
//------ 修复草图模块循环依赖 开始------
import {isAbstractSketchEdgeObject} from '../guards/isAbstractSketchEdgeObject';
import {isSketchCollectionObject} from '../guards/isSketchCollectionObject';
import {isSketchPointObject} from '../guards/isSketchPointObject';
import {isSketchSlotObject} from '../guards/isSketchSlotObject';
//------ 修复草图模块循环依赖 结束------
import {
    LineMaterialFactory,
    PointsMaterialFactory,
    SketchPointCloneMaterialFallbackKind,
} from '../../material';
import { addObject3DToParent } from '../../utils/scene-object/addObject3DToParent';
import {resolveObject3DSceneAncestor} from '../../utils/scene-object/resolveObject3DSceneAncestor';

/**
 * 网格拖动留影代理。
 * 职责：
 * 1. 在拖动开始时克隆实体并渲染“拖动前状态”；
 * 2. 在拖动结束时移除留影对象；
 * 3. 将留影外观统一处理为半透明，便于与当前实体区分。
 */
export class SketchSnapshot extends THREE.Group{
    /** 留影透明度（对标 SolidWorks 拖动时的半透明原实体观感，原曲线明显浅于预览曲线）。 */
    private static readonly SNAPSHOT_OPACITY = 0.35;

    /** 当前留影对象。 */
    private snapshotObject: THREE.Object3D | null = null;

    /** 当前留影独占材质集合。 */
    private readonly ownedMaterials = new Set<THREE.Material>();

    /** 当前留影独占几何体集合。 */
    private readonly ownedGeometries = new Set<THREE.BufferGeometry>();

    /**
     * 以源对象当前状态创建并显示留影。
     * @param source 源对象。
     */
    public show(source: THREE.Object3D): void {
        const scene = resolveObject3DSceneAncestor(source);
        if (!scene) {
            throw new Error('源对象未挂载到场景，无法显示拖动留影。');
        }
        this.hide();
        const clonedObject = this.createSnapshotObjectByMeshType(source);
        clonedObject.name = `${source.name || source.uuid}-drag-snapshot`;
        addObject3DToParent(clonedObject, scene);
        this.snapshotObject = clonedObject;
    }

    /**
     * 隐藏并移除当前留影。
     */
    public hide(): void {
        if (this.snapshotObject?.parent) {
            this.snapshotObject.parent.remove(this.snapshotObject);
        }
        this.snapshotObject = null;

        // 留影材质和克隆几何体只服务当前一次拖拽，移除场景后必须立即释放，避免连续拖动积累 GPU 资源。
        this.ownedMaterials.forEach((material) => material.dispose());
        this.ownedMaterials.clear();
        this.ownedGeometries.forEach((geometry) => geometry.dispose());
        this.ownedGeometries.clear();
    }

    /**
     * 根据草图网格类型创建留影对象。
     * @param source 源对象。
     * @returns 留影对象。
     */
    private createSnapshotObjectByMeshType(source: THREE.Object3D): THREE.Object3D {
        if (isSketchPointObject(source)) {
            /**
             * 椭圆轴端点在几何上仍是控制点，但视觉语义与普通草图点一致，使用雪花形状。
             * 留影必须按视觉语义解析，不能仅依据 isControlPoint 回退成圆形，否则按下任一轴端点时
             * 四个关联轴端点都会叠加圆形留影，造成四个点同时发生变化的错误观感。
             */
            const pointFallbackKind = source.isEllipseAxisControlPoint() || !source.isControlPoint
                ? SketchPointCloneMaterialFallbackKind.SketchPoint
                : SketchPointCloneMaterialFallbackKind.ControlPoint;
            const snapshotMaterial = source.isCurveCenterControlPoint
                ? PointsMaterialFactory.createCurveCenterPointDragSnapshotMaterial()
                : PointsMaterialFactory.createDragSnapshotPointMaterial(pointFallbackKind);
            // 点材质工厂只负责创建默认材质，留影透明度由当前代理按场景语义统一设置。
            snapshotMaterial.opacity = SketchSnapshot.SNAPSHOT_OPACITY;
            this.ownedMaterials.add(snapshotMaterial);
            const snapshotPoint = new THREE.Sprite(snapshotMaterial);
            snapshotPoint.position.copy(source.position);
            snapshotPoint.quaternion.copy(source.quaternion);
            snapshotPoint.scale.copy(source.scale);
            snapshotPoint.renderOrder = source.renderOrder;
            // 留影必须继承拖拽开始瞬间的显示状态，禁止把圆心、象限点等隐藏辅助点意外显示出来。
            snapshotPoint.visible = source.visible;
            return snapshotPoint;
        }

        if (isAbstractSketchEdgeObject(source)) {
            const snapshotGeometry = source.geometry.clone();
            const snapshotMaterial = LineMaterialFactory.createDragSnapshotLineMaterial(
                SketchSnapshot.SNAPSHOT_OPACITY,
            );
            this.ownedGeometries.add(snapshotGeometry);
            this.ownedMaterials.add(snapshotMaterial);
            const snapshotLine = new THREE.Line(
                snapshotGeometry,
                snapshotMaterial,
            );
            // 快照会被挂到源对象所属场景或复合集合快照中，必须保留源对象的局部变换。
            snapshotLine.position.copy(source.position);
            snapshotLine.quaternion.copy(source.quaternion);
            snapshotLine.scale.copy(source.scale);
            snapshotLine.renderOrder = source.renderOrder;
            // 集合和槽口会递归克隆成员边，隐藏成员边同样不能因快照创建而显形。
            snapshotLine.visible = source.visible;
            return snapshotLine;
        }
        if (isSketchCollectionObject(source)) {
            const group = new THREE.Group();
            const snapshotEdges = new Map<string, THREE.Object3D>();

            source.collectSet.forEach((collectSource) => {
                snapshotEdges.set(collectSource.uuid, collectSource);
            });
            source.children.forEach((child) => {
                // 多边形辅助圆等派生成员不在 collectSet 中，但会随集合一起移动，也必须进入起始状态快照。
                if (isAbstractSketchEdgeObject(child)) {
                    snapshotEdges.set(child.uuid, child);
                }
            });
            snapshotEdges.forEach((edge) => {
                group.add(this.createSnapshotObjectByMeshType(edge));
            });

            group.position.copy(source.position);
            group.quaternion.copy(source.quaternion);
            group.scale.copy(source.scale);
            group.renderOrder = source.renderOrder;
            group.visible = source.visible;
            return group;
        }
        //------ 槽口留影 开始------
        if (isSketchSlotObject(source)) {
            const group = new THREE.Group();
            // 遍历槽口的 4 条边，递归创建留影。
            source.getEdges().forEach((edge) => {
                group.add(this.createSnapshotObjectByMeshType(edge));
            });
            group.position.copy(source.position);
            group.quaternion.copy(source.quaternion);
            group.scale.copy(source.scale);
            group.renderOrder = source.renderOrder;
            group.visible = source.visible;
            return group;
        }
        //------ 槽口留影 结束------
        return source.clone(true);
    }
}
